import { useEffect, useState } from "react";
import { IPrincipal, ITableColumn } from "@interfaces/common.interfaces";
import ReportSection from "../../../reports-common/report-section/report-section";
import { IReportSectionProps } from "../../../reports-common/report-section/report-section.props";
import { IRequestModel, IRequestModelUser } from "@interfaces/reports/reports-common.interfaces";
import { ISampleInTenantReportDetails, ISampleInTenantReportItem } from "@interfaces/reports/sample-reports.interfaces";
import SampleInTenantReportSummary from "components/reports/sample/sample-in-tenant-report/sample-in-tenant-report-summary";
import { IGuestUsersInWorkspacesByOwnerReportDetails, IGuestUsersInWorkspacesByOwnerReportItem } from "@interfaces/reports/guest-user-reports.interfaces";
import GuestUsersInWorkspacesByOwnerReportSummary from "components/reports/guest-users/guest-users-in-workspaces-by-owner/guest-users-in-workspaces-by-owner-report-summary";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import PrincipalPicker from "components/controls/principal-picker/principal-picker";
import { getWorkspaceRoleTypeKey, getWorkspaceTypeKey } from "lib/workspaces";
import CheckedCell from "components/reports-common/report-results-grid-cells/checked-cell/checked-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import { usePPA } from "context/ppa-context";
import { PPAPermissions } from "lib/permissions";

// TODO: Somewhere central. e.g. in the navitems????
const reportUrl: string = "/api/reports/guest-users-in-workspaces-by-owner";

export default function GuestUsersInWorkspacesByOwnerReport() {
    const { currentUser, hasPermission } = usePPA();
    const { t } = useL10n<ILocaleStrings>();
    const [model, setModel] = useState<IRequestModel>({}); // { owner?: IRequestModelUser } //IRequestModel
    const [selectedUser, setSelectedUser] = useState<IPrincipal>();
    const [mineOnly, setMineOnly] = useState<boolean>(false);

    // TODO: Each column defined somewhere central and put together here.
    const cols: Array<ITableColumn<IGuestUsersInWorkspacesByOwnerReportItem>> = [
        { key: "displayName", label: t("displayName"), showInGrid: true, showInExport: true, minWidth: 100 },
        { key: "email", label: t("email"), showInGrid: true, showInExport: true, minWidth: 100, idealWidth: 200 },
        { key: "upn", label: t("upn"), showInGrid: false, showInExport: true, minWidth: 100, idealWidth: 300 },
        { key: "objectId", label: t("entraUserId"), showInGrid: false, showInExport: true },
        { key: "memberOfName", label: t("name"), showInGrid: true, showInExport: true, minWidth: 200, idealWidth: 300 },
        { key: "memberOfUrl", label: t("url"), showInGrid: true, showInExport: true, minWidth: 200, idealWidth: 400 },
        { key: "memberOfId", label: t("id"), showInGrid: false, showInExport: true, minWidth: 100 },
        {
            key: "memberOfType", label: t("type"), showInGrid: true, showInExport: true, minWidth: 100,
            renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (t(getWorkspaceTypeKey(item.memberOfType))),
            getExportValue: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (t(getWorkspaceTypeKey(item.memberOfType)))
        }, // TODO: Maybe this is an icon column?
        //{ key: "groupType", label: "Group type", showInGrid: true, showInExport: true, minWidth: 100 },
        //{ key: "groupName", label: "Group name", showInGrid: true, showInExport: true, minWidth: 100 },
        {
            key: "memberOfRole", label: t("role"), showInGrid: true, showInExport: true, minWidth: 100,
            renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => {
                return item.memberOfRole ? t(getWorkspaceRoleTypeKey(item.memberOfRole)) : ""
            },
            getExportValue: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => {
                return item.memberOfRole ? t(getWorkspaceRoleTypeKey(item.memberOfRole)) : ""
            }
        },
        //{ key: "customPermission", label: "Custom permission", showInGrid: true, showInExport: true, minWidth: 150 },
        { key: "created", label: t("created"), showInGrid: true, showInExport: true, renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (<DateTimeCell columnId={key} item={item} pattern="PPp" />) },
        { key: "deleted", label: t("deleted"), showInGrid: false, showInExport: true, renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (<DateTimeCell columnId={key} item={item} pattern="PPp" />) },
        {
            key: "pendingAcceptance", label: t("pending"), showInGrid: true, showInExport: true,
            renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (<CheckedCell columnId={key} item={item} checkedValue={true} />),
            getExportValue: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => {
                return item.pendingAcceptance ? t("yes") : t("no");
            }
        },
        {
            key: "accountDisabled", label: t("disabled"), showInGrid: true, showInExport: true,
            renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (<CheckedCell columnId={key} item={item} checkedValue={true} />),
            getExportValue: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => {
                return item.accountDisabled ? t("yes") : t("no");
            }
        },
        {
            key: "orphaned", label: t("orphaned"), showInGrid: true, showInExport: true,
            renderCell: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => (<CheckedCell columnId={key} item={item} checkedValue={true} />),
            getExportValue: (key: string, item: IGuestUsersInWorkspacesByOwnerReportItem) => {
                return item.orphaned ? t("yes") : t("no");
            }
        },
        { key: "lastLogin", label: t("lastLogin"), showInGrid: false, showInExport: false } // TODO: Only if data available/P1 licence. Maybe a flag in settings? Or check the tenant settings for P1 lic?
    ];

    useEffect(() => {
        setSelectedUser(currentUser);
        if (hasPermission(PPAPermissions.RequestGuestUsersInWorkspacesByOwnerReportMineOnly) && !hasPermission(PPAPermissions.RequestGuestUsersInWorkspacesByOwnerReportAny))
            setMineOnly(true);
    }, []);

    const onUserChange = (value?: IPrincipal) => {
        console.log(`GuestUserReport -> onUserChange -> `, value);
        setModel({ ...model, owner: { upn: value?.upn } });
        setSelectedUser(value);
    }

    const isModelValid = (): boolean => model.owner?.upn ? true : false;

    // TODO: This will also need the IRequestModel for each report type as another T property...
    const reportSectionProps: IReportSectionProps<IGuestUsersInWorkspacesByOwnerReportItem, IGuestUsersInWorkspacesByOwnerReportDetails> = {
        columns: cols,
        summaryComponent: <></>,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaGuestUsersInWorkspacesByOwnerReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaGuestUsersInWorkspacesByOwnerReportDefaultExportFilenamePrefix"
    }

    return (
        <ReportSection {...reportSectionProps}>
            <PrincipalPicker
                onChange={onUserChange}
                model={selectedUser}
                label="ppaWorkspaceRoleTypeOwner"
                readonly={mineOnly}
                principalTypes={["User"]} />
        </ReportSection>
    );
}
